import { Injectable, NgZone } from '@angular/core';
import { AuthenticationService } from 'src/app/shared/services/authentication.service';
import { FeatureFlagService } from './feature-flag.service';
import { FeatureFlags } from '../feature-flags';
import { MessageService } from 'primeng/api';
import { environment } from 'src/environments/environment';

const AUTO_LOGOUT_DURATION_MS = 15 * 60 * 1000; // 15 minutes
const CHECK_INTERVAL_MS = 1000; // 1 second
const ONE_MINUTE_MS = 60 * 1000;
const key = 'lastActivityTime'; 

@Injectable({
  providedIn: 'root'
})
export class AutoLogoutService {
  private passwordSecurityEnabled = false;

  private set lastActivityTime(value: number) {
    localStorage.setItem(key, value.toString());
  }

  private get lastActivityTime() {
    return parseInt(localStorage.getItem(key));
  }

  private intervalId: any = null;

  constructor(
    private ngZone: NgZone,
    private messageService: MessageService,
    private authService: AuthenticationService,
    private featureFlagService: FeatureFlagService
  ) { }

  startTracking(): void {

    this.featureFlagService.getFeatureFlag(FeatureFlags.PasswordSecurity).subscribe({
      next: (res) => {
        this.passwordSecurityEnabled = res.responseModel?.isEnabled ?? false;
        this.initSessionTracking();
      },
      error: () => {
        this.passwordSecurityEnabled = false;
        this.initSessionTracking();
      }
    });
  }

  stopTracking(): void {
    this.removeEventListeners();
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
  }

  private initSessionTracking(): void {
    this.lastActivityTime = Date.now();
    this.addEventListeners();

    this.ngZone.runOutsideAngular(() => {
      this.intervalId = setInterval(() => this.checkSession(), CHECK_INTERVAL_MS);
    });
  }

  private checkSession(): void {
    const now = Date.now();
    const sessionExpiry = this.lastActivityTime + AUTO_LOGOUT_DURATION_MS;

    if (!this.passwordSecurityEnabled) {
      if (now > sessionExpiry) {
        this.ngZone.run(() => this.promptSessionExpired());
      }
    } else {
      const warnThreshold = sessionExpiry - ONE_MINUTE_MS;
      if (now > warnThreshold) {
        this.ngZone.run(() => this.promptSessionWarning(now, sessionExpiry));
      }
    }
  }

  private closeAllPopups(): void {
    const pDialogSelectors = [
      '.p-dialog-mask',
      '.p-dialog',
      '[role="dialog"]',
      '.p-component-overlay-enter'
    ];
    
    pDialogSelectors.forEach(selector => {
      const dialogs = document.querySelectorAll(selector);
      dialogs.forEach(dialog => {
        const closeSelectors = [
          '.p-dialog-header-close',
          '.p-dialog-close-button',
          '.close',
          '[aria-label="Close"]',
          '.pi-times',
          '.p-button-icon-only'
        ];
        
        for (const closeSelector of closeSelectors) {
          const closeButton = dialog.querySelector(closeSelector);
          if (closeButton) {
            (closeButton as HTMLElement).click();
            break;
          }
        }
        
        if (dialog.parentNode) {
          dialog.remove();
        }
      });
    });

    const overlaySelectors = [
      '.p-component-overlay',
      '.p-dialog-mask',
      '.modal-backdrop',
      '.overlay',
      '.popup-overlay',
      '.p-overlaypanel',
      '.p-sidebar-mask',
      '.p-dropdown-panel',
      '.p-calendar-panel'
    ];
    
    overlaySelectors.forEach(selector => {
      const overlays = document.querySelectorAll(selector);
      overlays.forEach(overlay => overlay.remove());
    });

    this.messageService.clear();

    const confirmDialogs = document.querySelectorAll('.p-confirmdialog, .p-confirm-dialog');
    confirmDialogs.forEach(dialog => {
      const closeButton = dialog.querySelector('.p-dialog-header-close, .p-button-text, .p-confirm-dialog-reject');
      if (closeButton) {
        (closeButton as HTMLElement).click();
      } else {
        dialog.remove();
      }
    });

    const bootstrapModals = document.querySelectorAll('.modal.show, .modal');
    bootstrapModals.forEach(modal => {
      const closeBtn = modal.querySelector('.btn-close, .close, [data-dismiss="modal"], [data-bs-dismiss="modal"]');
      if (closeBtn) {
        (closeBtn as HTMLElement).click();
      }
      
      modal.classList.remove('show', 'fade');
      (modal as HTMLElement).style.display = 'none';
      
      const backdrop = document.querySelector('.modal-backdrop');
      if (backdrop) backdrop.remove();
    });

    const customPopupSelectors = [
      '.popup',
      '.dialog',
      '.overlay',
      '.modal-overlay',
      '[class*="popup"]',
      '[class*="modal"]',
      '[class*="dialog"]',
      '.payment-popup',
      '.collect-payment'
    ];
    
    customPopupSelectors.forEach(selector => {
      const popups = document.querySelectorAll(selector);
      popups.forEach(popup => {
        const closeBtns = popup.querySelectorAll('.close, .btn-close, [aria-label="Close"], .cancel-btn, .x-button');
        closeBtns.forEach(btn => (btn as HTMLElement).click());
        
        popup.classList.remove('show', 'active', 'open', 'visible');
        (popup as HTMLElement).style.display = 'none';
        
        const computedStyle = window.getComputedStyle(popup as HTMLElement);
        if (computedStyle.display !== 'none' && computedStyle.visibility !== 'hidden') {
          popup.remove();
        }
      });
    });

    const bodyClasses = [
      'p-overflow-hidden',
      'modal-open',
      'p-dialog-open',
      'no-scroll',
      'overlay-open',
      'popup-open'
    ];
    
    bodyClasses.forEach(className => {
      document.body.classList.remove(className);
    });

    document.body.style.removeProperty('overflow');
    document.body.style.removeProperty('padding-right');

    const matDialogs = document.querySelectorAll('.cdk-overlay-container .cdk-overlay-pane');
    matDialogs.forEach(dialog => {
      const closeButton = dialog.querySelector('[mat-dialog-close], .mat-dialog-close');
      if (closeButton) {
        (closeButton as HTMLElement).click();
      }
    });

    const overlayContainers = document.querySelectorAll('.cdk-overlay-container, .p-component-overlay-enter');
    overlayContainers.forEach(container => {
      if (container.children.length > 0) {
        container.innerHTML = '';
      }
    });

    document.dispatchEvent(new CustomEvent('session-timeout-cleanup'));
    
    setTimeout(() => {
      const remainingPopups = document.querySelectorAll('.p-dialog-mask, .modal.show, .popup, [role="dialog"], .payment-popup');
      remainingPopups.forEach(popup => {
        popup.remove();
      });
      
      document.body.className = document.body.className.replace(/p-overflow-hidden|modal-open|popup-open/g, '').trim();
    }, 100);
  }

  private promptSessionExpired(): void {
    this.closeAllPopups();
    const continueSession = confirm('Your session has timed out. Do you want to continue?');
    if (continueSession) {
      this.resetSession();
    } else {
      this.logout();
    }
  }

  private promptSessionWarning(currentTime: number, expiryTime: number): void {
    this.closeAllPopups();
    const extendSession = confirm(`Your session will expire in 1 minute due to inactivity. Click 'Ok' to continue and 'Cancel' to logout.`);

    if (extendSession) {
      if (Date.now() > expiryTime) {
        this.showInfoMessage('Your session has expired due to inactivity. Please log in again.');
        this.logout();
      } else {
        this.resetSession();
      }
    } else {
      this.logout();
    }
  }

  private resetSession(): void {
    const now = Date.now();
    this.lastActivityTime = now;
  }

  private logout(): void {
    this.closeAllPopups();
    this.stopTracking();
    this.authService.signOut();
  }

  private showInfoMessage(message: string, title: string = 'Info', lifeTime = environment.errorMessageLifeTime): void {
    this.messageService.clear();
    this.messageService.add({
      severity: 'info',
      summary: title,
      detail: message,
      life: lifeTime
    });
  }

  private addEventListeners(): void {
    this.ngZone.runOutsideAngular(() => {
      document.body.addEventListener('click', this.resetSession.bind(this));
      document.body.addEventListener('mousemove', this.resetSession.bind(this));
    });
  }

  private removeEventListeners(): void {
    document.body.removeEventListener('click', this.resetSession.bind(this));
    document.body.removeEventListener('mousemove', this.resetSession.bind(this));
  }
}